import { SharedModule } from '@/app/shared/shared.module';
import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../shared/services/auth.service';
import { ForgotPwdComponent } from './forgot-pwd/forgot-pwd.component';
import { VerifyOtpComponent } from './forgot-pwd/verify-otp/verify-otp.component';
import { ChangePwdComponent } from './forgot-pwd/change-pwd/change-pwd.component';
import {
  UserProxy,
  CountryProxy,
  SigninUserCommand,
  SigninUserResponseResponseResult,
  LookupProxy,
  ConfigurationProxy,
  CountryViewModel,
  ValidateUserOTPCommand,
  BooleanResponseResult,
} from '@/app/proxy/proxies';
import { HeaderComponent } from '../shared/header/header.component';
import { ExternalProviderType, OtpReasonTypes } from '../shared/auth-enums';
import { AppInitializerService } from '@/app/core/services/app-initializer.service';
import { UnifiedSsoService } from '../shared/services/unified-sso.service';
import { SSOProvider, SSOUserData } from '../shared/interfaces/sso.interface';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmOtpComponent } from '@/app/features/shared/components/confirm-otp/confirm-otp.component';
import { UserService } from '@/app/core/services/user.service';
import { PasswordService } from '../shared/services/password.service';
import { findDefaultPhoneCountry } from '../shared/default-phone-country';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { LanguageInitializerService } from '@/app/core/services/language-initializer.service';
@Component({
  selector: 'app-login',
  imports: [SharedModule, ForgotPwdComponent, VerifyOtpComponent, ChangePwdComponent, HeaderComponent, ConfirmOtpComponent],
  providers: [UserProxy, CountryProxy, LookupProxy, ConfigurationProxy],
  templateUrl: './login.component.html',
  styleUrl: './login.component.less',
})
export class LoginComponent implements OnInit, OnDestroy {
  private static readonly FALLBACK_PASSWORD_MAX_LENGTH = 128;

  private readonly _mixpanelTracker = inject(MixpanelTrackerService);
  private readonly _languageInitializerService = inject(LanguageInitializerService);

  private subscription = new Subscription();
  @ViewChild(ConfirmOtpComponent) confirmOtpComponent!: ConfirmOtpComponent;

  /**
   * From IAM policy via PasswordService.getPasswordRuleset(); fallback until policy loads.
   * Enforced on paste via ngModelChange, not only HTML maxlength.
   */
  passwordMaxLength: number;

  email: string = '';
  password: string = '';

  loginInputType: 'email' | 'phone' = 'phone';

  phoneNumber: number;
  selectedCountry: any;

  externalProviderTypes = ExternalProviderType;
  countries: CountryViewModel[] = [];

  languages = [
    { name: 'English', code: 'en' },
    { name: 'Spanish', code: 'es' },
    { name: 'French', code: 'fr' },
    { name: 'German', code: 'de' },
    { name: 'Italian', code: 'it' },
  ];
  selectedLanguage = this.languages[0];
  logoPath = 'doktar-logo.png';

  passwordFocused: boolean = false;
  isForgotPwd: boolean = false;
  public FORGOT_PWD_STEP = {
    LOGIN: 'login',
    VERIFY_OTP: 'verify-otp',
    CHANGE_PWD: 'change-pwd',
    FORGOT_PWD: 'forgot-pwd',
  };
  forgotPwdStep: string = this.FORGOT_PWD_STEP.LOGIN;

  verifyOtpInfo: string = '';

  selectedLanguageGuid: string = '';

  somethingWentWrong: boolean = false;
  errorMessage: string = '';

  loggingIn: boolean = false;

  constructor(
    private _location: Location,
    private _router: Router,
    private _route: ActivatedRoute,
    private _authDataService: AuthService,
    private _userProxy: UserProxy,
    private _appInitializerService: AppInitializerService,
    private _unifiedSsoService: UnifiedSsoService,
    private _dialogService: DialogService,
    private _userService: UserService,
    private _passwordService: PasswordService
  ) {
    this.passwordMaxLength = this.resolvePasswordMaxLengthFromPolicy();
  }

  ngOnInit(): void {
    this.countries = [...(this._appInitializerService.appStartupData?.countries ?? [])];
    this.countries.sort((a, b) => parseInt(a.phoneCode) - parseInt(b.phoneCode));
    this.selectedCountry = findDefaultPhoneCountry(this.countries);

    // Check for forgot password step from query parameters or route
    this._route.queryParams.subscribe((params) => {
      if (params['step'] === 'forgot-pwd') {
        this.forgotPwdStep = this.FORGOT_PWD_STEP.FORGOT_PWD;
      }

      // Handle SSO errors
      if (params['error'] === 'sso_failed' || params['error'] === 'callback_failed') {
        this.somethingWentWrong = true;
        this.errorMessage = params['message'] || 'SSO authentication failed. Please try again.';
      }
    });

    // Check if we're on the forgot-password route
    if (this._router.url.includes('/auth/login/forgot-password')) {
      this.forgotPwdStep = this.FORGOT_PWD_STEP.FORGOT_PWD;
    }

    // Initialize SSO providers
    this.initializeSSOProviders();

    this.subscription.add(
      this._passwordService
        .getPasswordRuleset()
        .pipe(map((rules) => this.maxLengthFromRules(rules)))
        .subscribe((max) => {
          this.passwordMaxLength = max;
          if (this.password.length > max) {
            this.password = this.password.slice(0, max);
          }
        })
    );
  }

  private async initializeSSOProviders(): Promise<void> {
    try {
      await this._unifiedSsoService.initializeAllProviders();
    } catch (error) {
      console.error('Failed to initialize SSO providers:', error);
    }
  }

  ngOnDestroy(): void {
    this.somethingWentWrong = false;
    this.errorMessage = '';
  }

  backButton() {
    this._location.back();
  }

  forgotPwdDirection() {
    this._authDataService.setEmail(this.email);
    this._authDataService.setPhoneNumber(this.phoneNumber?.toString());
    this.forgotPwdStep = this.FORGOT_PWD_STEP.FORGOT_PWD;
  }

  goToRegister() {
    this._router.navigate(['/auth/register']);
  }

  login(
    externalProviderType: ExternalProviderType | undefined,
    externalProviderToken: string | undefined,
    externalProviderInformations: SSOUserData | undefined
  ) {
    this.loggingIn = true;
    let user: SigninUserCommand;
    if (externalProviderType) {
      user = new SigninUserCommand({
        email: externalProviderInformations?.email,
        phone: '',
        phoneCode: '',
        password: '',
        newPassword: undefined,
        externalProviderType: externalProviderType,
        externalProviderToken: externalProviderToken,
        captchaToken: undefined,
      });
    } else {
      const contact = this.activeContact();
      user = new SigninUserCommand({
        email: contact.email,
        phone: contact.phone,
        phoneCode: contact.phoneCode,
        password: this.clampPasswordForSubmit(this.password),
        newPassword: undefined,
        externalProviderType: undefined,
        externalProviderToken: undefined,
        captchaToken: undefined,
      });
    }

    this._userProxy.user_Signin(user).subscribe({
      next: (res: SigninUserResponseResponseResult) => {
        if (res.isSuccess) {
          this.handleSigninResponse(res);
          this._authDataService.handleSigninResponse(res);
        } else if (res.errors.some((error) => error.code === 'E1011')) {
          this.openConfirmOtpComponent();
        } else {
          this.somethingWentWrong = true;
          this.errorMessage = res.errors[0].message.split(':')[1];

          this._mixpanelTracker.trackLoginFailed({
            'failure reason': this.buildSigninFailureReason(res.errors),
          });
        }
        this.loggingIn = false;
      },
    });
  }

  handleSigninResponse(res: SigninUserResponseResponseResult) {
    this._authDataService.handleSigninResponse(res);

    this._userService.loadUserProfile().subscribe({
      next: () => {
        this.trackLoggedInAndRedirect();
      },
      error: (error) => {
        console.error('Failed to load user profile after login:', error);
        this.trackLoggedInAndRedirect();
      },
    });
  }

  private trackLoggedInAndRedirect(): void {
    // Prefer Doktar app language stored in localStorage (languageISO1),
    // fallback to LanguageInitializerService, then to 'en'
    const languageFromStorage = localStorage.getItem('languageISO1');
    const currentLanguage = languageFromStorage || this._languageInitializerService.getCurrentLanguageCode() || 'en';
    this._mixpanelTracker.trackLoggedIn(
      {},
      {
        language: currentLanguage
      }
    );
    this._authDataService.handlePostLoginRedirect();
  }

  openConfirmOtpComponent() {
    const contact = this.activeContact();
    this.confirmOtpComponent.show(OtpReasonTypes.VerifyAccount, undefined, contact.email, contact.phone);
    this.subscription.add(
      this.confirmOtpComponent.emitOtpModal.subscribe((otpModalEmitter: number) => {
        if(otpModalEmitter != null){
          this._userProxy
            .user_ValidateOTP(
              new ValidateUserOTPCommand({
                id: undefined,
                email: contact.email,
                phone: contact.phone,
                phoneCode: contact.phoneCode,
                otp: otpModalEmitter ?? 0,
                otpReason: OtpReasonTypes.VerifyAccount.valueOf(),
              })
            )
            .subscribe((res: BooleanResponseResult) => {
              if (res.isSuccess && res.result) {
                this.login(undefined, undefined, undefined);
              } else {
                this.openConfirmOtpComponent();
              }
            });
        }
      })
    );
  }

  async onSSOButtonClick(provider: string) {
    let ssoProvider: SSOProvider;

    // Map external provider type to SSO provider
    switch (provider) {
      case ExternalProviderType.Apple:
        ssoProvider = SSOProvider.APPLE;
        break;
      case ExternalProviderType.Google:
        ssoProvider = SSOProvider.GOOGLE;
        break;
      case ExternalProviderType.Microsoft:
        ssoProvider = SSOProvider.MICROSOFT;
        break;
      case ExternalProviderType.Facebook:
        ssoProvider = SSOProvider.FACEBOOK;
        break;
      default:
        console.error('Unknown SSO provider:', provider);
        return;
    }

    // Check if provider is available
    if (!this._unifiedSsoService.isProviderAvailable(ssoProvider)) {
      console.error(`SSO provider ${ssoProvider} is not available`);
      return;
    }

    console.log(`Starting SSO authentication with ${ssoProvider}...`);

    // For SSO authentication, we need to redirect to the provider's OAuth flow
    // The provider will redirect back to our callback URL with the authentication result
    try {
      // Authenticate with the provider (this will trigger the OAuth redirect)
      this._unifiedSsoService.authenticate(ssoProvider).subscribe({
        next: (response) => {
          // This should not be reached for OAuth redirect flows
          // The user should be redirected to the OAuth provider
          this.login(provider, response.token, response.userData);
          console.log('SSO authentication response:', response);
        },
        error: (error) => {
          console.error(`SSO authentication error for ${ssoProvider}:`, error);
          // Show error to user
          this.somethingWentWrong = true;
          this.errorMessage = error.message || 'SSO authentication failed';
        },
      });
    } catch (error) {
      console.error(`Error starting SSO authentication for ${ssoProvider}:`, error);
      this.somethingWentWrong = true;
      this.errorMessage = 'Failed to start SSO authentication';
    }
  }

  /** Max length from the loaded IAM policy (sync snapshot used in the constructor). */
  private resolvePasswordMaxLengthFromPolicy(): number {
    return this.maxLengthFromRules(this._passwordService.getCurrentPasswordPolicy() ?? undefined);
  }

  /** Maps policy `maximumLength` to a positive cap, or the app fallback when the policy is missing or invalid. */
  private maxLengthFromRules(rules: { maximumLength?: number } | null | undefined): number {
    const max = rules?.maximumLength;
    return max != null && max > 0 ? max : LoginComponent.FALLBACK_PASSWORD_MAX_LENGTH;
  }

  /**
   * Keeps `password` within `passwordMaxLength` on every model update.
   * Needed because paste and some inputs can bypass HTML `maxLength` / PrimeNG limits.
   */
  onPasswordNgModelChange(value: string | null): void {
    const v = value ?? '';
    const max = this.passwordMaxLength;
    if (v.length > max) {
      this.password = v.slice(0, max);
    }
  }

  /** Ensures the string sent on sign-in never exceeds the current cap (defense in depth). */
  private clampPasswordForSubmit(raw: string): string {
    const max = this.passwordMaxLength;
    return raw.length > max ? raw.slice(0, max) : raw;
  }

  changeInputType() {
    this.loginInputType = this.loginInputType === 'email' ? 'phone' : 'email';
  }

  /** Sign-in checks the phone number first, so the unused identifier stays empty. */
  private activeContact(): { email: string; phone: string; phoneCode: string } {
    if (this.loginInputType === 'email') {
      return { email: this.email ?? '', phone: '', phoneCode: '' };
    }

    return {
      email: '',
      phone: this.phoneNumber?.toString().replace(/-/g, '') ?? '',
      phoneCode: this.selectedCountry?.phoneCode?.replace(/\+/g, '') ?? '',
    };
  }

  onSteperChange(event: string) {
    this.forgotPwdStep = event as keyof typeof this.FORGOT_PWD_STEP;
  }

  onVerifyOtpInfo(event: string) {
    this.verifyOtpInfo = event;
  }

  onSelectedLanguageGuid(event: string) {
    this.selectedLanguageGuid = event;
  }

  /**
   * Single string for Mixpanel "failure reason" (code + backend message).
   */
  private buildSigninFailureReason(errors: { code?: string; message?: string }[]): string {
    const e = errors?.[0];
    if (!e) return 'unknown';
    const code = e.code?.trim();
    const msg = (e.message ?? '').trim();
    if (code && msg.startsWith(`${code}:`)) return msg;
    if (code && msg) return `${code}: ${msg}`;
    return msg || code || 'unknown';
  }
}
